import Restore from "@mui/icons-material/Restore";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { first } from "lodash-es";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { formatDatetime, fromISO8601 } from "../../../../utils/iso8601";
import { useMomentLog } from "../../hooks/use-moment-log";

import type { MomentLogRecord } from "../../server/supabase-moment-log-service";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import type { ButtonProps } from "@mui/material";
import type { FC } from "react";

export const MomentLogV2: FC<
  ButtonProps & {
    moment: Pick<MomentFormMoment, "id" | "source_moment_id" | "discover_moment_launched_from">;
    showText?: boolean;
  }
> = ({ moment, showText = true, ...rest }) => {
  const { data: momentLog } = useMomentLog(moment.id);
  const dialogController = useDialog();

  const mostRecent = first(momentLog);

  if (!momentLog || momentLog.length === 0 || !mostRecent) return null;

  return (
    <>
      {showText ? (
        <Button
          data-analytics-id="edit-history-button"
          variant="text"
          onClick={dialogController.handleOpen}
          startIcon={<Restore />}
          {...rest}
        >
          Edit History
        </Button>
      ) : (
        <Tooltip
          title={<Box sx={{ textAlign: "center" }}>Communication History</Box>}
          placement="top"
          arrow
        >
          <IconButton
            data-analytics-id="edit-history-button"
            onClick={dialogController.handleOpen}
            {...rest}
          >
            <Restore />
          </IconButton>
        </Tooltip>
      )}
      <Dialog open={dialogController.open} onClose={dialogController.handleClose} fullWidth>
        <DialogTitle display="flex" component="div">
          Edit History
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1">
            {momentLog.map((record, i) => (
              <Typography key={i} variant="body2" data-testid={`audit-log-${i}`}>
                <LogEntry record={record} moment={moment} />
              </Typography>
            ))}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

const LogEntry: FC<{
  record: MomentLogRecord;
  moment: Pick<MomentFormMoment, "id" | "source_moment_id" | "discover_moment_launched_from">;
}> = ({ record, moment }) => {
  const str = `${record.action} by ${record.user_name ?? "Unknown"} at ${
    record.created_at ? formatDatetime(fromISO8601(record.created_at)) : "Unknown"
  }`;

  if (moment.source_moment_id !== null && record.action.startsWith("Copied")) {
    return <Link href={`/moments/${moment.source_moment_id}`}>{str}</Link>;
  }

  if (record.action.includes("Discover") && moment.discover_moment_launched_from) {
    return (
      <Link href={`/discover/search?q=${encodeURIComponent(moment.discover_moment_launched_from)}`}>
        {str}
      </Link>
    );
  }

  return <>{str}</>;
};
